import { useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import {
  Phone,
  MapPin,
  Building2,
  Briefcase,
  Search,
  Mail,
  Users,
} from 'lucide-react'
import { useCompanyDirectory } from '../../hooks/useCompanyDirectory.js'
import { useUserType } from '../../hooks/useUserType.js'

function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) {
      const first = parts[0]?.[0] ?? ''
      const second = parts[1]?.[0] ?? ''
      return (first + second).toUpperCase()
    }
    if (parts.length === 1) {
      return (parts[0] ?? email).slice(0, 2).toUpperCase()
    }
  }
  return email.slice(0, 2).toUpperCase()
}

function getAvatarColor(email: string): string {
  const colors = [
    'bg-[#3366FF]',
    'bg-emerald-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-cyan-500',
    'bg-violet-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-teal-500',
    'bg-indigo-500',
  ]
  let hash = 0
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length] ?? 'bg-[#3366FF]'
}

export default function CompanyDirectoryPage(): JSX.Element {
  const { entries, loading, error } = useCompanyDirectory()
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { showSoftphone } = useUserType()

  const filtered = entries.filter((e) => {
    const term = search.toLowerCase()
    return (
      (e.name?.toLowerCase().includes(term) ?? false) ||
      e.email.toLowerCase().includes(term) ||
      (showSoftphone && (e.extension?.includes(term) ?? false)) ||
      (e.department?.toLowerCase().includes(term) ?? false) ||
      (e.branch?.toLowerCase().includes(term) ?? false)
    )
  })

  return (
    <div className="p-6 bg-[#F8F9FB] min-h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#0B1120]">Company Directory</h1>
          <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {entries.length} employee{entries.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder={showSoftphone ? 'Search by name, email, extension, department...' : 'Search by name, email, department...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-[3px] text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[#3366FF] focus:ring-1 focus:ring-[#3366FF]/20 w-80 transition-all"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-[3px] p-5 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-slate-200 rounded" />
                  <div className="h-3 w-48 bg-slate-200 rounded" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 w-full bg-slate-200 rounded" />
                <div className="h-3 w-3/4 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-rose-50 border border-rose-200 rounded-[3px] p-6 text-center">
          <p className="text-rose-700 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && !error && (
        <>
          {filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-[3px] p-12 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-50 mb-3">
                <Search className="h-6 w-6 text-slate-400" />
              </div>
              <h3 className="text-sm font-semibold text-slate-700">No results found</h3>
              <p className="text-xs text-slate-500 mt-1">Try adjusting your search</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((entry) => (
                <div
                  key={entry.id}
                  onClick={() => navigate(`/agent/company-directory/${entry.id}`, {
                    state: { tabLabel: entry.loginid ?? entry.name ?? entry.email },
                  })}
                  className="bg-white border border-slate-200 rounded-[3px] p-5 hover:shadow-md hover:border-slate-300 transition-all group relative cursor-pointer"
                >
                  {/* Top row: avatar + name/email */}
                  <div className="flex items-start gap-4">
                    <div
                      className={`h-14 w-14 rounded-full ${getAvatarColor(entry.email)} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}
                    >
                      {entry.avatarUrl ? (
                        <img
                          src={entry.avatarUrl}
                          alt={entry.name ?? entry.email}
                          className="h-14 w-14 rounded-full object-cover"
                        />
                      ) : (
                        <span>{getInitials(entry.name, entry.email)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 truncate">
                        {entry.name ?? entry.email}
                      </h3>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{entry.email}</span>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="mt-4 space-y-2">
                    {entry.location && (
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-500">Location:</span>
                        <span className="truncate">{entry.location}</span>
                      </div>
                    )}
                    {entry.branch && (
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-500">Branch:</span>
                        <span className="truncate">{entry.branch}</span>
                      </div>
                    )}
                    {entry.department && (
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Briefcase className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-500">Department:</span>
                        <span className="truncate">{entry.department}</span>
                      </div>
                    )}
                    {entry.extension && showSoftphone && (
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Phone className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-500">Extension:</span>
                        <span className="font-mono text-[#3366FF] font-semibold">{entry.extension}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
